/**
 * Shared formatting helpers for patient-statement UI, dialogs, and print.
 */

export const formatCurrency = (amount: string, currency: string = 'USD'): string => {
    const num = parseFloat(amount);
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
    }).format(num);
};

/** Format a YYYY-MM-DD (or similar) date string as a local calendar date. */
export const formatStatementDate = (dateString: string): string => {
    if (!dateString) {
        return '—';
    }

    try {
        const [year, month, day] = dateString.split('-').map(Number);
        const date = new Date(year, month - 1, day);

        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    } catch {
        return '—';
    }
};

/** Parse money from string/number; strips `$` and commas. Treats blank / em-dash as 0. */
export const parseMoney = (val?: string | number | null): number => {
    if (val === null || val === undefined) return 0;
    if (typeof val === 'number') return Number.isFinite(val) ? val : 0;
    const s = String(val).trim();
    if (!s || s === '—') return 0;
    const num = parseFloat(s.replace(/[$,]/g, ''));
    return Number.isFinite(num) ? num : 0;
};

/** @deprecated Prefer parseMoney — alias kept for dialog call sites. */
export const parseLedgerAmount = parseMoney;

export const formatMoney = (amount: number, opts?: { negative?: boolean }): string => {
    const value = opts?.negative && amount > 0 ? -amount : amount;
    return formatCurrency(String(value.toFixed(2)));
};

/** @deprecated Prefer formatMoney — alias kept for Apply-to UI. */
export const formatLedgerMoney = (amount: number): string => formatMoney(amount);

/** Print ledger cells: empty string when zero; optional credit formatting. */
export const formatMoneyOrEmpty = (raw: string, opts?: { asCredit?: boolean }): string => {
    if (!raw || parseMoney(raw) === 0) return '';
    const n = parseMoney(raw);
    const abs = Math.abs(n);
    const formatted = formatCurrency(abs.toFixed(2));
    if (opts?.asCredit) {
        return n > 0 ? `−${formatted.replace(/^\$/, '')}` : formatted;
    }
    return n < 0 ? `−${formatted.replace(/^\$/, '')}` : formatted;
};

export const escapeHtml = (text: string | undefined | null): string => {
    if (text === null || text === undefined) return '';
    return String(text).replace(/[&<>"']/g, m => {
        const map: Record<string, string> = {
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            '"': '&quot;',
            "'": '&#039;',
        };
        return map[m] || m;
    });
};
